import _ from "lodash";
import { computed } from "mobx";
import { observer } from "mobx-react-lite";
import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { useLeadStore, usePipedriveStore, useSearchStore } from "../..";
import useBodyContainerClassName from "../../hooks/useBodyContainerClassName";
import useLeadNoteDelete from "../../hooks/useLeadNoteDelete";
import useNavigateToCompany from "../../hooks/useNavigateToCompany";
import SearchType from "../../interfaces/SearchType";
import { leadOwnershipTypeToString, leadStatusToString } from "../../utils/enumUtils";
import { formatDate, formatNumber, formatPrice } from "../../utils/utils";
import AttachmentList from "../AttachmentList";
import commonStyles from "../Common.module.css";
import detailsStyles from "../Details.module.css";
import FieldRow from "../FieldRow";
import Note from "../Note";
import { showErrorToast, showSuccessToast } from "../ToastContainer";
import AddLeadNote from "./AddLeadNote";

function LeadDetailsDetailsTab() {
    const { id } = useParams();
    const leadStore = useLeadStore();
    const pipedriveStore = usePipedriveStore();
    const searchStore = useSearchStore();
    const lead = useMemo(() => computed(() => leadStore.leads.find(l => l.id.toString() === id)!), [id]).get();
    const activity = useMemo(() => computed(() => pipedriveStore.activities.find(a => a.id === lead.activityId)), [lead]).get();
    const pipedriveOrganization = useMemo(() => computed(() => pipedriveStore.organizations.find(o => o.id === lead.pipedriveCompanyId)), [lead]).get();
    const labelOption = useMemo(() => computed(() => pipedriveStore.labelOptions.find(o => o.id === lead.labelId)), [lead]).get();
    const navigateToCompany = useNavigateToCompany();
    const deleteLeadNote = useLeadNoteDelete();
    const bodyContainerClassName = useBodyContainerClassName();

    const onUploadAttachments = async (files: File[]) => {
        try {
            for (const file of files) {
                await leadStore.uploadAttachment(lead.id, file);
            }

            showSuccessToast();
        } catch (e) {
            showErrorToast();
        }
    };

    const onDeleteAttachment = async (key: string) => {
        try {
            await leadStore.deleteAttachment(lead.id, key);
            showSuccessToast();
        } catch (e) {
            showErrorToast();
        }
    };

    return (
        <div className={bodyContainerClassName}>
            <div className={detailsStyles.columns}>
                <div className={[detailsStyles.column, commonStyles.rowSeparated].join(" ")}>
                    <div className={commonStyles.header}>Lead Details</div>
                    <FieldRow label="STATUS">
                        {leadStatusToString(lead.status)}
                    </FieldRow>
                    <FieldRow label="LABEL">
                        {labelOption && labelOption.label}
                    </FieldRow>
                    <FieldRow label="OWNER NAME">
                        {lead.ownerName && (
                            <span className={commonStyles.link} onClick={() => lead.ownerName && searchStore.setSearch(lead.ownerName, SearchType.Leads)}>
                                {lead.ownerName}
                            </span>
                        )}
                    </FieldRow>
                    <FieldRow label="OWNERSHIP TYPE">
                        {!!lead.ownershipType && leadOwnershipTypeToString(lead.ownershipType)}
                    </FieldRow>
                    <FieldRow label="ZONING">
                        {lead.zoning}
                    </FieldRow>
                    <FieldRow label="LAND SIZE">
                        {!!lead.landSize && `${formatNumber(lead.landSize)} Acres`}
                    </FieldRow>
                    <FieldRow label="USABLE LAND SIZE">
                        {!!lead.usableArea && `${formatNumber(lead.usableArea)} Acres`}
                    </FieldRow>
                    <FieldRow label="BUILDING SIZE">
                        {!!lead.buildingSize && `${formatNumber(lead.buildingSize)} Sq-Ft`}
                    </FieldRow>
                    <FieldRow label="LOT COVERAGE">
                        {!!lead.lotCoverage && `${lead.lotCoverage}%`}
                    </FieldRow>
                    <FieldRow label="LOCATION GRADE">
                        {lead.locationGrade}
                    </FieldRow>
                    <FieldRow label="PROPERTY GRADE">
                        {lead.propertyGrade}
                    </FieldRow>
                    <FieldRow label="ASSESSMENT">
                        {!!lead.totalAssessment && formatPrice(lead.totalAssessment)}
                    </FieldRow>
                    <FieldRow label="OWNER ADDRESS">
                        {lead.ownerAddress && (
                            <span className={commonStyles.link} onClick={() => lead.ownerAddress && searchStore.setSearch(lead.ownerAddress, SearchType.Leads)}>
                                {lead.ownerAddress}
                            </span>
                        )}
                    </FieldRow>
                    <FieldRow label="LAST SOLD">
                        {(lead.saleDate || lead.salePrice) && [
                            !!lead.saleDate && formatDate(lead.saleDate),
                            !!lead.salePrice && formatPrice(lead.salePrice),
                        ].filter(s => s).join(": ")}
                    </FieldRow>
                </div>
                <div className={[detailsStyles.column, commonStyles.rowSeparated].join(" ")}>
                    <div className={commonStyles.header}>Contacts</div>
                    <FieldRow label="COMPANY">
                        {pipedriveOrganization && (
                            <span className={commonStyles.link} onClick={() => navigateToCompany(pipedriveOrganization.id)}>
                                {pipedriveOrganization.name}
                            </span>
                        )}
                    </FieldRow>
                    <FieldRow label="CONTACT NAME">
                        {lead.contactName}
                    </FieldRow>
                    <FieldRow label="EMAIL">
                        {lead.email}
                    </FieldRow>
                    <FieldRow label="PHONE #">
                        {lead.phoneNumber}
                    </FieldRow>
                    <FieldRow label="REMINDER">
                        {activity && formatDate(activity.dueDate)}
                    </FieldRow>
                    <FieldRow label="ACTIVITY">
                        {activity && activity.subject}
                    </FieldRow>
                </div>
            </div>
            <div className={detailsStyles.columns}>
                <div className={detailsStyles.column}>
                    <div className={commonStyles.header}>Attachments</div>
                    <AttachmentList attachments={leadStore.leadIdsAttachmentLists[lead.id] || []} onUpload={onUploadAttachments} onDelete={onDeleteAttachment} />
                </div>
            </div>
            <div className={detailsStyles.columns}>
                <div className={detailsStyles.column}>
                    <div className={commonStyles.header}>Comments</div>
                    <AddLeadNote leadId={lead.id} />
                    {_.orderBy(lead.notes, n => n.createdAt, "desc").map(note => (
                        <Note
                            key={note.id}
                            userName={note.userName}
                            text={note.text}
                            createdAt={note.createdAt}
                            onDelete={() => deleteLeadNote(lead.id, note.id)}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
}

export default observer(LeadDetailsDetailsTab);
